// CRM 2.0 · M12 Template Builder: calls to the Sales App backend (routes/templates.js).
// Every call sends the login token; the server checks the "Templates" permissions itself.
import axios from "axios";
import { supabase } from "../supabaseClient";

const base = () => process.env.REACT_APP_PAYMENT_LINK_URL;
const auth = () => ({ Authorization: `Bearer ${supabase.auth.session()?.access_token}` });

// Returns data, or throws an Error with the server's message (and .details for a list of problems).
async function call(method, path, { data, params, headers } = {}) {
  try {
    const res = await axios({ method, url: `${base()}${path}`, data, params, headers: { ...auth(), ...headers } });
    return res.data.data;
  } catch (e) {
    const body = e.response?.data;
    const err = new Error(body?.message || e.message);
    err.details = body?.details;
    err.status = e.response?.status;
    throw err;
  }
}

export const listTemplates = (status) => call("get", "templates", { params: { status } });
export const getTemplateVersion = (id) => call("get", `template-versions/${id}`);
export const getTemplateFileLink = (id) => call("get", `template-versions/${id}/file`);
export const getTemplatePage = (id) => call("get", `template-versions/${id}/page`);
// Buyer card: { action: SET, blockKey, anchors } | { action: CLEAR, blockKey } | { action: MODE, buyerMode }
export const changeTemplateBlocks = (id, change) => call("put", `template-versions/${id}/blocks`, { data: change });
// page fixes (DC-202): { tool, target, anchor, start, end, newText, blankNo }; both return the new page
export const addTemplateEdit = (id, edit) => call("post", `template-versions/${id}/edits`, { data: edit });
export const undoTemplateEdit = (id, editId) => call("delete", `template-versions/${id}/edits/${editId}`);
// Templates › Delete draft: one unused draft version, or every version of a template (all unused drafts)
export const deleteTemplateDraft = (id) => call("delete", `template-versions/${id}`);
export const deleteTemplate = (templateGroupId) => call("delete", `templates/${templateGroupId}`);
// Pages & QR (part 3a): the sections with their page rule / QR; changes [{ docTemplateSectionId, rule?, qrOn?, qrView? }]
export const getTemplateSections = (id) => call("get", `template-versions/${id}/sections`);
export const saveTemplateSections = (id, changes) => call("put", `template-versions/${id}/sections`, { data: { changes } });
// "Buyer details" in one click: what the system finds (near = a clicked line), apply it, Undo
export const suggestBuyerDetails = (id, near) => call("get", `template-versions/${id}/buyer-details`, { params: { near } });
export const applyBuyerDetails = (id, near, fields) => call("post", `template-versions/${id}/buyer-details`, { data: { near, fields } });
export const undoBuyerDetails = (id) => call("delete", `template-versions/${id}/buyer-details`);
// "Make this the applicant list" on the first applicant line; remove = its Undo; both return the page and all blanks
export const makeApplicantList = (id, anchor, fields) => call("post", `template-versions/${id}/applicant-list`, { data: { anchor, fields } });
export const undoApplicantList = (id) => call("delete", `template-versions/${id}/applicant-list`);
// a read Replace file / Edit in Word note: { list: "REASSIGN" | "RECHECK", blankNo, added }
export const clearCarryNote = (id, note) => call("post", `template-versions/${id}/carry-notes/clear`, { data: note });
export const listDocFields = (kind) => call("get", "doc-fields", { params: { kind } });
export const getTemplateBuilderSettings = () => call("get", "doc-settings/template-builder");
// Settings permission; lastUpdatedAt from the read (someone else saving in between -> 409)
export const saveTemplateBuilderSettings = (values, lastUpdatedAt) => call("put", "doc-settings/template-builder", { data: { values, lastUpdatedAt } });

export function uploadTemplate({ file, templateName, templateTypeKey }) {
  const form = new FormData();
  form.append("file", file);
  form.append("templateName", templateName);
  form.append("templateTypeKey", templateTypeKey);
  return call("post", "templates", { data: form });
}

// Replace file: a new version of the template; settings are carried over by the server
export function replaceTemplateFile(versionId, file) {
  const form = new FormData();
  form.append("file", file);
  return call("post", `templates/${versionId}/versions`, { data: form });
}

// M12-F3 Step 2 · Templates › Booking drafts (test page): CRM 2.0 bookings, one booking's template and drafts,
// make a review / final draft ({ documentTypeKey, reason, customerVisible }), a download link for one draft
export const listBookingDrafts = (search) => call("get", "booking-drafts", { params: { search } });
export const getBookingDrafts = (bookingId) => call("get", `booking-drafts/${bookingId}`);
export const getBookingDraftsMode = () => call("get", "booking-drafts-mode");
export const makeBookingDraft = (bookingId, body) => call("post", `booking-drafts/${bookingId}`, { data: body });
export const getBookingDocumentLink = (docId, print) => call("get", `booking-documents/${docId}/file`, { params: print ? { print: 1 } : undefined });
// "Print top sheet again" for the current final draft or printed copy (DC-225)
export const printTopSheetAgain = (docId, reason) => call("post", `booking-documents/${docId}/top-sheet`, { data: { reason } });
export const handCopyToSignatory = (copyId, signatoryUserId) => call("post", `booking-drafts/copies/${copyId}/hand-to-signatory`, { data: { signatoryUserId } });
export const listSignatories = () => call("get", "booking-drafts-signatories");
// Reissue allotment letter (DC-237): the pop-up's facts (current Allotment No., reasons, warning, allowed)
export const getReissueInfo = (bookingId) => call("get", `booking-drafts/${bookingId}/reissue`);
export const listRoles = () => call("get", "doc-settings/roles");

// Recover a deleted draft (Templates › Status: Deleted)
export const recoverTemplateDraft = (versionId) => call("post", `template-versions/${versionId}/recover`);
// Make a copy: a new template (its own name, Draft v1) with the version's file and every setting
export const copyTemplate = (versionId, templateName) => call("post", `template-versions/${versionId}/copy`, { data: { templateName } });
// Edit as new version: the next Draft of the same template from the version's own file, settings carried over
export const newTemplateVersion = (versionId) => call("post", `template-versions/${versionId}/new-version`);

// Edit in Word (DC-206): open = { docsUrl, config, lock } for the ONLYOFFICE editor; save = the new version
// { docTemplateId, versionNo, carried, needsReassign, needsRecheck } or { unchanged: true }; close without saving
export const openEditInWord = (id) => call("post", `template-versions/${id}/edit-in-word`);
export const saveEditInWord = (id) => call("post", `template-versions/${id}/edit-in-word/save`);
export const closeEditInWord = (id) => call("post", `template-versions/${id}/edit-in-word/close`);

// Preview PDF (sample values, page fixes, watermark); a long agreement takes about half a minute
export async function getTemplatePreviewPdf(id, applicants) {
  try {
    const res = await axios({ method: "get", url: `${base()}template-versions/${id}/preview.pdf`, params: { applicants }, headers: auth(), responseType: "blob", timeout: 150000 });
    // DC-204: blanks that print a 2nd–4th applicant the sample booking does not have
    // Pages & QR (part 3b): { s: { orderNo: start page }, q: [QR pages], k: [QRs left out], n: [printed as New page] }
    let map = null;
    try { map = JSON.parse(decodeURIComponent(res.headers["x-preview-map"] || "null")); } catch (x) { map = null; }
    return { blob: res.data, warnings: decodeURIComponent(res.headers["x-preview-warnings"] || ""), pages: Number(res.headers["x-preview-pages"]) || null, map };
  } catch (e) {
    let message = e.code === "ECONNABORTED" ? "The preview took too long and was stopped. Try again; if it happens again, tell the tech team." : e.message;
    try { if (e.response?.data) message = JSON.parse(await e.response.data.text()).message || message; } catch (x) { /* not a JSON error */ }
    throw new Error(message);
  }
}

// Step 3 QR: the same print as JSON { pdf (base64), pages, warnings, map: { s: { orderNo: start page },
// f: { page: [field keys] }, q: [QR pages], k: [QRs left out] } }
export async function getTemplatePageMap(id, applicants) {
  try {
    const res = await axios({ method: "get", url: `${base()}template-versions/${id}/preview.pdf`, params: { applicants, format: "json" }, headers: auth(), timeout: 150000 });
    return res.data.data;
  } catch (e) {
    throw new Error(e.code === "ECONNABORTED" ? "Working out the pages took too long and was stopped. Try again; if it happens again, tell the tech team." : e.response?.data?.message || e.message);
  }
}

// Bookings to preview with: the template's linked projects, or a search (unit no., customer name, tower, project):
// { scope: { kind LINKED | ALL | SEARCH, projects, why }, bookings: [{ allotmentPaymentId, project, tower, unitNo, customer, status, crm2, landowner, ended, linked }] }
export const getPreviewBookings = (id, search) => call("get", `template-versions/${id}/preview-bookings`, { params: { search } });

// Step 4 "Preview with booking": { pdf (base64), booking, check: { pages, missing, used, newPages, warnings }, warnings };
// read only on the server (nothing saved)
export async function getBookingPreview(id, allotmentPaymentId) {
  try {
    const res = await axios({ method: "get", url: `${base()}template-versions/${id}/booking-preview`, params: { allotmentPaymentId }, headers: auth(), timeout: 180000 });
    return res.data.data;
  } catch (e) {
    throw new Error(e.code === "ECONNABORTED" ? "The preview took too long and was stopped. Try again; if it happens again, tell the tech team." : e.response?.data?.message || e.message);
  }
}

// page setup (DC-203): { topIn, bottomIn, insideIn, outsideIn, mirrored, tableMode } or { reset: true }
export const savePageSetup = (id, setup) => call("put", `template-versions/${id}/page-setup`, { data: setup });

export const setBlankSettings = (id, blanks) => call("put", `template-versions/${id}/blanks`, { data: { blanks } });
export const acceptSuggestions = (id) => call("post", `template-versions/${id}/accept-suggestions`);
export const sendForApproval = (id, note) => call("post", `template-versions/${id}/send`, { data: { note } });
// previewedWith: { allotmentPaymentId } of the booking the approver previewed it with (Approve needs it when
// TEMPLATE_BUILDER.approvalNeedsPreview is on)
export const approveTemplate = (id, comments, previewedWith) => call("post", `template-versions/${id}/approve`, { data: { comments, previewedWith } });
export const returnTemplate = (id, comments, previewedWith) => call("post", `template-versions/${id}/return`, { data: { comments, previewedWith } });
// M12-F3 · Project › Template Mapping › CRM 2.0 bookings: plans = the project's FarVision plans [{ id, name }]
const plansParam = (plans) => (plans || []).map((p) => `${p.id}:${String(p.name || "").replace(/[|]/g, "/")}`).join("|");
// kind: AGREEMENT (tower + plan rows) or TOP_SHEET (one row per project, DC-225)
export const getTemplateLinks = (projectId, plans, kind = "AGREEMENT") => call("get", `template-links/${projectId}`, { params: { kind, plans: plansParam(plans) } });
// Templates › Company defaults (the top sheet): GET every kind; PUT one (docTemplateId null removes it, confirmRemove after the warning)
export const getCompanyDefaults = () => call("get", "template-company-links");
export const saveCompanyDefault = (kind, docTemplateId, confirmRemove) => call("put", `template-company-links/${kind}`, { data: { docTemplateId, confirmRemove } });
export const saveTemplateLinks = (projectId, links, plans, kind = "AGREEMENT") => call("put", `template-links/${projectId}`, { data: { kind, links, plans } });
